/**
 * "Choose where to receive your payout" — shown only while a payout of the client's
 * has no destination (a fund redemption names none; a returned transfer needs a new
 * one). Where the money lands is the client's decision: no member of staff can make
 * it for them, so this card is the only place it can be made.
 */
import { useCallback, useEffect, useState } from 'react';
import bankAccountsService, { type ClientBankAccount } from '../../services/bankAccounts.service';
import clientPayoutsService, { type AwaitingPayout } from '../../services/clientPayouts.service';
import { splitError, type MoneyRefusal } from '../../lib/moneyRefusal';
import RefusalNotice from './RefusalNotice';
import { colors } from '../../styles/theme';
import { eligibleAccounts, needingDestination } from './payoutDestination.helpers';

export default function PayoutDestinationCard(): JSX.Element | null {
  const [payouts, setPayouts] = useState<AwaitingPayout[]>([]);
  const [accounts, setAccounts] = useState<ClientBankAccount[]>([]);
  // Accounts that could not be loaded are not "no account": the client would be told to
  // add one they already have.
  const [accountsUnreadable, setAccountsUnreadable] = useState(false);

  const reload = useCallback(() => {
    clientPayoutsService.awaiting()
      .then((rows) => setPayouts(needingDestination(rows)))
      // Silent: the card is an invitation, not a status page. A client with nothing
      // pending, or not yet approved, simply never sees it.
      .catch(() => setPayouts([]));
  }, []);

  useEffect(() => { reload(); }, [reload]);
  const loadAccounts = useCallback(() => {
    bankAccountsService.listMine()
      .then((rows) => {
        setAccounts(rows);
        setAccountsUnreadable(false);
      })
      .catch(() => {
        setAccounts([]);
        setAccountsUnreadable(true);
      });
  }, []);
  useEffect(() => {
    if (payouts.length === 0) return;
    loadAccounts();
  }, [payouts.length, loadAccounts]);

  if (payouts.length === 0) return null;

  return (
    <section
      aria-label="Payouts waiting for a destination"
      className="rounded-lg p-4 mb-4"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <h2 className="text-sm font-semibold mb-1" style={{ color: colors.ink }}>
        Choose where to receive your payout
      </h2>
      <p className="text-xs mb-3" style={{ color: colors.inkSoft }}>
        Only you can decide which of your bank accounts receives your money. We will send
        it once you choose.
      </p>
      {payouts.map((p) => (
        <PayoutRow
          key={p.payout_id}
          payout={p}
          accounts={eligibleAccounts(p, accounts)}
          accountsUnreadable={accountsUnreadable}
          onRetryAccounts={loadAccounts}
          onDone={reload}
        />
      ))}
    </section>
  );
}

function PayoutRow({ payout, accounts, accountsUnreadable, onRetryAccounts, onDone }: Readonly<{
  payout: AwaitingPayout;
  accounts: ClientBankAccount[];
  accountsUnreadable: boolean;
  onRetryAccounts: () => void;
  onDone: () => void;
}>): JSX.Element {
  const [accountId, setAccountId] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<MoneyRefusal | null>(null);
  const ready = accountId !== '' && code.length >= 6 && !busy;

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setRefusal(null);
    try {
      await clientPayoutsService.chooseDestination(payout, accountId, code);
      onDone();
    } catch (e: unknown) {
      // A cooling-off account or one that never funded is not a failure to save —
      // it is an answer, and it gets the state written for it.
      const split = splitError(e, 'We could not save your choice. Please try again.');
      setRefusal(split.refusal);
      setError(split.message);
    } finally {
      setBusy(false);
      setCode('');
    }
  };

  const inputStyle = { border: `1px solid ${colors.border}`, background: colors.surface, color: colors.ink };

  return (
    <div className="mb-3 last:mb-0">
      <p className="text-sm mb-2" style={{ color: colors.ink }}>
        {payout.net_amount} {payout.currency_code}
      </p>
      {payout.management_fee_amount && (
        <p className="text-xs mb-2" style={{ color: colors.inkSoft }}>
          Management fee deducted: {payout.management_fee_amount} {payout.currency_code}
        </p>
      )}
      {refusal && <RefusalNotice refusal={refusal} />}
      {error && (
        <div className="rounded-lg p-2 mb-2 text-xs" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}
      {accountsUnreadable && (
        <p className="text-xs" role="alert" style={{ color: colors.inkSoft }}>
          We could not load your bank accounts just now.{' '}
          <button type="button" onClick={onRetryAccounts} className="underline font-semibold">
            Try again
          </button>
        </p>
      )}
      {!accountsUnreadable && (accounts.length === 0 ? (
        <p className="text-xs" style={{ color: colors.inkSoft }}>
          You have no active bank account in {payout.currency_code}. Add one in your profile;
          for your protection a new account can receive a payout after a few business days.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <select aria-label="Destination account" value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  className="px-3 py-2 rounded-lg text-sm" style={inputStyle}>
            <option value="">— choose account —</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.account_type} ····{a.account_number_last4}{a.nickname ? ` (${a.nickname})` : ''}
              </option>
            ))}
          </select>
          <input type="text" inputMode="numeric" autoComplete="one-time-code"
                 aria-label="Authentication code" placeholder="Authenticator code"
                 value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                 className="px-3 py-2 rounded-lg text-sm font-mono" style={inputStyle} />
          <button type="button" disabled={!ready} onClick={() => void submit()}
                  className="px-3 py-2 rounded-lg text-sm font-semibold"
                  style={{ background: colors.navy, color: '#FFFFFF', opacity: ready ? 1 : 0.5 }}>
            {busy ? 'Saving…' : 'Send it here'}
          </button>
        </div>
      ))}
    </div>
  );
}
